import { Skeleton } from "../../shadcn-components/ui/skeleton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../shadcn-components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogClose,
} from "../../shadcn-components/ui/dialog";
import {
  HiOutlineUserCircle,
  HiOutlinePhone,
  HiOutlineMail,
} from "react-icons/hi";
import {
  HiOutlineDocumentText,
  HiOutlineCheckCircle,
  HiOutlineClock,
} from "react-icons/hi2";
import { FiArrowRightCircle } from "react-icons/fi";
import { useEffect, useState, useCallback } from "react";
import userData from "../../api/user";
import baseUrl from "../../config/baseUrl";
import getUserDocumentsTimeSummary from "../../api/getUserDocumentsTimeSummary";

function UserCard({ token, customBoxShadow }) {
  const [countLoading, setCountLoading] = useState(true);
  const [userCards, setUserCards] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTimeSummary, setUserTimeSummary] = useState(null);
  const [loadingTimeSummary, setLoadingTimeSummary] = useState(false);

  const userDetailsData = async () => {
    const url = `${baseUrl}/api/get-user-documents-summary`;
    const response = await userData({ token, api: url });

    if (response.code === 200) {
      const cards = response.data.map((user) => ({
        id: user.user_id,
        user_name: user.assign_user,
        assigned_today: user.total_assigned_today,
        processed_documents: user.processed_count,
        processed_today: user.processed_count_today,
        pending_documents: user.pending_count,
        email: user.email,
        phone: user.phone_number,
      }));
      setUserCards(cards);
    }
  };

  const handleUserCardClick = useCallback(
    async (user) => {
      setSelectedUser(user);
      setLoadingTimeSummary(true);
      setUserTimeSummary(null);

      try {
        const response = await getUserDocumentsTimeSummary({
          token,
          userId: user.id,
        });

        if (response.code === 200) {
          setUserTimeSummary(response.data);
        }
      } catch (err) {
        console.error("Error fetching summary:", err);
      }

      setLoadingTimeSummary(false);
    },
    [token]
  );

  useEffect(() => {
    const fetchData = async () => {
      setCountLoading(true);
      await userDetailsData();
      setCountLoading(false);
    };
    fetchData();
  }, []);

  const formatNumber = (num) => Intl.NumberFormat().format(num ?? 0);

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 w-full">
        {countLoading
          ? Array.from({ length: 12 }).map((_, index) => (
              <Skeleton key={index} className="h-40 w-full" />
            ))
          : userCards.length === 0 && <p>No users found.</p>}

        {userCards.map((user) => (
          <div
            key={user.id}
            className="w-full cursor-pointer"
            onClick={() => handleUserCardClick(user)}
          >
            <Card
              className="w-full hover:bg-[#f9fbff] transition-shadow hover:shadow-lg"
              style={customBoxShadow}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-2">
                  <HiOutlineUserCircle className="text-xl text-blue-600" />
                  <CardTitle className="text-base font-semibold">
                    {user.user_name}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 pt-2 text-sm text-gray-700">
                <div className="flex items-center gap-2">
                  <HiOutlineDocumentText className="text-lg text-gray-500" />
                  <span className="font-medium">Assigned Today:</span>{" "}
                  {formatNumber(user.assigned_today)}
                </div>
                <div className="flex items-center gap-2">
                  <HiOutlineCheckCircle className="text-lg text-green-600" />
                  <span className="font-medium">Processed:</span>{" "}
                  {formatNumber(user.processed_documents)}
                  <span className="ml-2 text-xs text-gray-500">
                    (Today: {formatNumber(user.processed_today)})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <HiOutlineClock className="text-lg text-yellow-500" />
                  <span className="font-medium">Pending:</span>{" "}
                  {formatNumber(user.pending_documents)}
                </div>
                <div className="flex justify-end pt-2">
                  <span className="flex items-center gap-1 text-blue-600 text-sm font-medium">
                    View Details <FiArrowRightCircle className="text-base" />
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      <Dialog
        open={!!selectedUser}
        onOpenChange={(open) => !open && setSelectedUser(null)}
      >
        <DialogContent className="w-full max-w-3xl min-h-[400px] p-6 rounded-lg shadow-xl">
          {selectedUser && (
            <>
              <div className="flex flex-col gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <HiOutlineUserCircle className="w-10 h-10 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedUser.user_name}
                  </h2>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-8 text-sm text-gray-600 pl-1">
                  {selectedUser.email && (
                    <div className="flex items-center gap-2">
                      <HiOutlineMail className="w-5 h-5 text-blue-500" />
                      <span className="text-base">{selectedUser.email}</span>
                    </div>
                  )}
                  {selectedUser.phone && (
                    <div className="flex items-center gap-2 mt-1 sm:mt-0">
                      <HiOutlinePhone className="w-5 h-5 text-green-600" />
                      <span className="text-base">{selectedUser.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {loadingTimeSummary ? (
                <div className="text-center py-10">
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : userTimeSummary ? (
                <div className="overflow-x-auto mt-2">
                  <table className="min-w-full text-sm text-left border border-gray-300">
                    <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                      <tr>
                        <th className="px-4 py-2 border">Documents</th>
                        <th className="px-4 py-2 border">Today</th>
                        <th className="px-4 py-2 border">This Week</th>
                        <th className="px-4 py-2 border">Last Week</th>
                        <th className="px-4 py-2 border">Older</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      {[
                        "assigned",
                        "processed",
                        "pending",
                        "clientUnknown",
                      ].map((type) => (
                        <tr key={type}>
                          <td className="px-4 py-2 border font-medium capitalize">
                            {type}{" "}
                            <span className="text-xs text-gray-500 font-normal">
                              (Total:{" "}
                              {formatNumber(userTimeSummary?.[`${type}_total`])}
                              )
                            </span>
                          </td>
                          <td className="px-4 py-2 border">
                            {formatNumber(userTimeSummary?.[`${type}_today`])}
                          </td>
                          <td className="px-4 py-2 border">
                            {formatNumber(
                              userTimeSummary?.[`${type}_this_week`]
                            )}
                          </td>
                          <td className="px-4 py-2 border">
                            {formatNumber(
                              userTimeSummary?.[`${type}_last_week`]
                            )}
                          </td>
                          <td className="px-4 py-2 border">
                            {formatNumber(userTimeSummary?.[`${type}_older`])}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6 text-red-500">
                  Could not load summary data.
                </div>
              )}

              <DialogFooter className="flex justify-end mt-6">
                <DialogClose asChild>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                    Close
                  </button>
                </DialogClose>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default UserCard;
